<template>
  <mdb-container>
    <div v-if="currentGroup">
      <banner-small :group="currentGroup" title="Document Library"/>
    </div>

    <editor-wizard v-if="showWizard && currentGroup" :group="currentGroup" :prefill="lastWizardData"
                   @cancel="showWizard = false"
                   @pageGenerated="wizardDone"/>

    <div v-if="currentGroup && !showEditor" class="text-left">
      <p v-if="documents.length===0">Your group doesn't have any documents yet.</p>
      <mdb-btn @click="create">
        <mdb-icon icon="plus"/>
        Create New Document
      </mdb-btn>
      <mdb-tbl responsive striped>
        <mdb-tbl-head color="primary-color" textWhite>
          <tr>
            <th>Title</th>
            <th>&nbsp;</th>
          </tr>
        </mdb-tbl-head>
        <mdb-tbl-body>
          <tr v-for="(doc,i) in documents" :key="doc.id">
            <td>{{ doc.title }}</td>
            <td>
              <mdb-btn floating icon="pencil" size="sm" tag="a" title="Edit" type="" @click.native="editDoc(doc)"/>
              <mdb-btn floating icon="eye" size="sm" tag="a" title="View" type="" @click.native="viewDoc(doc)"/>
              <mdb-btn color="danger" floating icon="trash" size="sm" tag="a" title="Delete" type=""
                       @click="deleteDoc(doc)"/>
            </td>
          </tr>
        </mdb-tbl-body>
      </mdb-tbl>
    </div>

    <mdb-modal :show="showViewer" info position="top-center" scrollable
               size="lg" @close="showViewer = false">
      <mdb-modal-header>
        <p class="heading">{{ currentDocument.title }}</p>
      </mdb-modal-header>
      <mdb-modal-body v-if="currentGroup">
        <div class="text-left" v-html="currentDocument.contentHtml"/>
      </mdb-modal-body>
      <mdb-modal-footer>
        <div class="small"><b>Authored:</b> {{ currentDocument.author }} {{ currentDocument.created }}</div>
        <div class="small"><b>Last Updated:</b> {{ currentDocument.lastUpdateUser }} {{ currentDocument.lastUpdated }}
        </div>
        <a :href="currentDocument.url" target="_blank">
          <mdb-btn rounded size="sm">
            <mdb-icon icon="external-link-alt"/>
            View as Page
          </mdb-btn>
        </a>
      </mdb-modal-footer>
    </mdb-modal>

    <div v-if="showEditor && !showWizard" class="text-left">
      <mdb-btn icon="arrow-left" size="sm" @click="showEditor = false">Back to List</mdb-btn>
      <mdb-btn icon="eye" size="sm" @click="viewUsage">View Where Used</mdb-btn>
      <mdb-card>
        <mdb-card-body>
          <div class="mb-4">
            <mdb-input id="documentTitle" v-model="currentDocument.title" label="Document Title"/>
            <mdb-input id="1" v-model="currentDocument.isPublic" label="Publicly Visible" type="checkbox"/>
          </div>
          <div class="mb-4 border">
            <!--div class="text-info small">Click in the Document Body to open the editor. You may paste images into the editor, and they will be placed in a "Pasted Images" folder in
              your image library.
              </div> -->
            <div class="formless-label">Document Body</div>
            <div id="text-wizard-area" class="grey lighten-5 border">
              <editor v-model="currentDocument.contentHtml" :init="editorConfig"/>
            </div>
            <div class="gp-end-wrap">&nbsp;</div>
            <mdb-btn icon="hat-wizard" rounded size="sm" @click="startWizard()">Use Page Wizard</mdb-btn>
          </div>
          <mdb-modal :show="showImageSelector" size="lg" @close="showImageSelector = false" class="gp-z-2000">
            <mdb-modal-header :close="true" center class="primary-color text-white">Select an Image
            </mdb-modal-header>
            <mdb-modal-body>
              <image-selector :group="currentGroup" emitOnSelection="imageSelected" @imageSelected="imageSelected"/>
            </mdb-modal-body>
          </mdb-modal>
          <mdb-btn :disabled="!currentDocument.title" @click="save">
            <mdb-icon icon="save"/>
            Save
          </mdb-btn>
          <mdb-btn @click="showEditor = false">
            <mdb-icon icon="window-close"/>
            Cancel
          </mdb-btn>
        </mdb-card-body>
      </mdb-card>
    </div>

    <mdb-modal :show="helpModal" class="animated" info position="top-left" removeBackdrop
               side @close="helpModal = false">
      <mdb-modal-header>
        <p class="heading">Hint</p>
      </mdb-modal-header>
      <mdb-modal-body v-if="currentGroup">
        <p class="text-left">The document library is a place to store text documents used by your group. Documents
          can either be served up as web pages associated with your group, or embedded in sections on your group's
          home page.
        </p>
      </mdb-modal-body>
    </mdb-modal>

    <yes-no-modal v-if="showConfirm" @no="showConfirm=false"
                  @yes="doDelete"
                  additional-text="This will also delete the document from event document sections, your home page and in any home page link sections. This cannot be undone."
                  />

    <yes-no-modal v-if="showWizardAck" additional-text="Existing page text will be overwritten."
                  @no="showWizardAck = false"
                  @yes="wizardAckDone"/>

    <mdb-modal v-if="usageModal" class="animated text-left" removeBackdrop
               @close="usageModal = false">
      <mdb-modal-header>
        <p class="heading">Document Usage</p>
      </mdb-modal-header>
      <mdb-modal-body v-if="currentGroup">

        <div
          v-if="documentUsage.homepage == 0 && documentUsage.events.length == 0 && documentUsage.homepageLinks.length == 0">
          Document is not in use on group home page, as home page links or event content.
        </div>

        <div v-if="documentUsage.homepage > 0">
          Document is used on the group home page ({{ documentUsage.homepage }}).
        </div>

        <div v-if="documentUsage.events.length > 0">
          <h5>Used on Events:</h5>
          <div v-for="event of documentUsage.events" :key="event.id">
            <router-link :to="'/event/show/' + event.eventId">{{event.eventName}}</router-link>
          </div>
        </div>

        <div v-if="documentUsage.homepageLinks.length > 0">
          <h5>Used on Homepage Link Section:</h5>
          <div v-for="link of documentUsage.homepageLinks" :key="link.id">
            {{link.title}}
          </div>
        </div>

      </mdb-modal-body>
    </mdb-modal>

  </mdb-container>

</template>
<script>
import draggable from "vuedraggable"
import api from '../../services/groupsPlaceApi.js'
import {mapState} from 'vuex'
import bannerSmall from '../group/BannerSmall'
import error from '../common/Error'
import editor from '@tinymce/tinymce-vue'
import imageSelector from '../group/ImageSelector'
import EditorWizard from "../common/EditorWizard";
import editorSettings from "../../services/editorSettings";
import YesNoModal from "../common/YesNoModal";

import {
  mdbBadge,
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardHeader,
  mdbCardImage,
  mdbCardText,
  mdbCardTitle,
  mdbCol,
  mdbContainer,
  mdbFileInput,
  mdbIcon,
  mdbInput,
  mdbMask,
  mdbModal,
  mdbModalBody,
  mdbModalFooter,
  mdbModalHeader,
  mdbRow,
  mdbTbl,
  mdbTblBody,
  mdbTblHead,
  mdbTooltip,
  mdbTreeview,
  mdbTreeviewItem,
  mdbView,
} from 'mdbvue';
import Vue from "vue";

export default {
  name: "group-documents",
  display: "Document Library",
  order: 1,
  components: {
    mdbContainer,
    mdbBtn,
    mdbRow,
    mdbCol,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardTitle,
    mdbCardText,
    mdbCardFooter,
    mdbCardImage,
    mdbMask,
    mdbModal,
    mdbModalHeader,
    mdbModalBody,
    mdbModalFooter,
    mdbIcon,
    mdbTreeview,
    mdbTreeviewItem,
    mdbBadge,
    mdbView,
    mdbTooltip,
    mdbInput,
    mdbFileInput,
    mdbTbl,
    mdbTblHead,
    mdbTblBody,
    draggable,
    bannerSmall,
    error,
    editor,
    imageSelector,
    EditorWizard,
    YesNoModal
  },
  computed: mapState({
                       currentGroup: state => state.currentGroup
                     }),
  async mounted() {
    await this.$store.dispatch("setExecuting", true)
    await api.groupBasics(this.$route.params.id, "perm:Edit Documents")
    document.title = "Documents"
    if (!this.currentGroup) {
      await this.$store.dispatch("setExecuting", false)
      this.$notify.error({message: "Could not access group " + this.$route.params.id, timeOut: 10000})
      return
    }
    this.editorConfig.file_picker_callback = this.myFileBrowser
    /*
     Uncomment when ready to enable the paste image feature. Will get usability results with admin pages first.
     this.editorConfig.paste_data_images = true
     this.editorConfig.images_upload_handler = this.handlePastedImage

     */

    api.get("/content/list/" + this.$route.params.id, {}).then(
      (details) => {
        this.documents = details.data
        if (this.documents.length == 0) this.helpModal = true
      },
      (error) => {
        this.$notify.error({message: "Error getting documents."})
      }).then(() => {
      this.$store.dispatch("setExecuting", false)
    })
  },
  data() {
    return {
      documents: [],
      showConfirm: false,
      deleteRequest: null,
      currentDocument: {title: "", url: "", html: ""},
      lastDeleted: null,
      showEditor: false,
      helpModal: false,
      usageModal: false,
      documentUsage: null,
      showViewer: false,
      showImageSelector: false,
      imageCallback: null,
      showWizard: false,
      editorConfig: editorSettings.theWorksInline(),
      showWizardAck: false,
      wizardAcknowledged: false,
      lastWizardData: null,
    }
  },
  methods: {
    handlePastedImage: function (blobInfo, success, failure, progress) {
      let url = "/image/create/?folder=pasted&group=" + this.currentGroup.vanityPath
      this.$store.dispatch("setExecuting", true)
      api.postImage(url, blobInfo.blob()).then(
        (results) => {
          success(results.data.image.url)
          let els = document.getElementsByClassName("mce-content-body")
          if (els.length > 0) {
            let el = els[0]
            let images = el.getElementsByTagName("img")
            for (let i of images) {
              if (!i.hasAttribute("width")) {
                i.setAttribute("width", "33%");
                i.classList.add("gp-user-image-left");
              }
            }
          }

        },
        (error) => {
          failure(error.response.data, {remove: true})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },

    create: function () {
      this.currentDocument = {id: null, title: "", isPublic: false, contentHtml: "<p></p>"}
      this.showWizard = true
      this.showEditor = true
    },
    save: function () {
      this.$store.dispatch("setExecuting", true)
      if (this.currentDocument.id) {
        api.post("/content/save/" + this.currentDocument.id, this.currentDocument).then(
          (details) => {
            this.$notify.success({message: "Document saved successfully."})
          },
          (error) => {
            this.$notify.error({message: "Error saving document."})
          }).then(() => {
          this.$store.dispatch("setExecuting", false)
        })
      } else {
        api.put("/content/create/" + this.currentGroup.vanityPath, this.currentDocument).then(
          (details) => {
            this.documents.unshift(details.data)
            this.currentDocument = this.documents[0]
            this.$notify.success({message: "Document saved successfully."})
          },
          (error) => {
            this.$notify.error({message: "Error saving document."})
          }).then(() => {
          this.$store.dispatch("setExecuting", false)
        })

      }

    },
    imageSelected: function (image) {
      this.showImageSelector = false
      let el = document.getElementsByClassName("tox-dialog-wrap")
      el[0].setAttribute("style", "display:block")
      Vue.nextTick().then(() => {
        this.imageCallback(image.url, {width: "33%", height: ""})
      })

    },
    myFileBrowser: function (callBack, value, meta) {
      let el = document.getElementsByClassName("tox-dialog-wrap")
      el[0].setAttribute("style", "display:none")
      Vue.nextTick().then(() => {
        this.showImageSelector = true
        this.imageCallback = callBack
      })
    },
    startWizard() {
      if (this.currentDocument.contentHtml) this.showWizardAck = true
      if (!this.showWizardAck) this.showWizard = true
    },
    wizardAckDone() {
      this.showWizardAck = false
      this.showWizard = true
    },
    wizardDone: function (result) {
      this.showWizard = false
      this.currentDocument.contentHtml = result.html
      this.lastWizardData = result
      if (!this.currentDocument.title) this.currentDocument.title = result.header
      this.save()
      this.$notify.success({message: "Page generated.", timeOut: 5000})
    },
    viewDoc: function (doc) {
      this.$store.dispatch("setExecuting", true)
      api.get("/content/show/" + doc.id, {}).then(
        (details) => {
          this.currentDocument = details.data
          this.showViewer = true
        },
        (error) => {
          this.$notify.error({message: "Error getting document."})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    viewUsage: function () {
      this.$store.dispatch("setExecuting", true)
      api.get("/content/usage/" + this.currentDocument.id, {}).then(
        (details) => {
          this.documentUsage = details.data
          this.usageModal = true
        },
        (error) => {
          this.$notify.error({message: "Error getting usage details."})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    editDoc: function (doc) {
      this.$store.dispatch("setExecuting", true)
      api.get("/content/show/" + doc.id, {}).then(
        (details) => {
          this.currentDocument = details.data
          this.showEditor = true
        },
        (error) => {
          this.$notify.error({message: "Error getting document."})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    deleteDoc: function (doc) {
      this.deleteRequest = doc
      this.showConfirm = true
    },
    doDelete: function () {
      this.showConfirm = false
      this.$store.dispatch("setExecuting", true)
      api.delete("/content/delete/" + this.deleteRequest.id, {}).then(
        (details) => {
          let loc = this.documents.indexOf(this.deleteRequest)
          this.documents.splice(loc, 1)
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 6000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    }
  }

};
</script>
